import { fetchPassengerTypeList } from "@/api/modules/passenger";

// while we wait for german support via the backend
function germanSingular(identifier, backup) {
    switch (identifier) {
        case "Adult":
            return "Erwachsener";
        case "Child":
            return "Kind";
        case "Senior":
            return "Rentner";
        default:
            return backup;
    }
}
function germanPlural(identifier, backup) {
    switch (identifier) {
        case "Adult":
            return "Erwachsene";
        case "Child":
            return "Kinder";
        case "Senior":
            return "Rentner";
        default:
            return backup;
    }
}

const PassengerTypeModel = function(passengerType, order) {
    this.order = order;
    this.id = passengerType.id;
    this.nameSingular = {
        da: passengerType.danishNameSingular,
        en: passengerType.englishNameSingular,
        de: germanSingular(
            passengerType.stringIdentifier,
            passengerType.englishNameSingular
        )
    };
    this.namePlural = {
        da: passengerType.danishNamePlural,
        en: passengerType.englishNamePlural,
        de: germanPlural(
            passengerType.stringIdentifier,
            passengerType.englishNamePlural
        )
    };
    this.minimumAge = passengerType.minimumAge;
    this.maximumAge = passengerType.maximumAge;
    this.contentPageIdentifier = passengerType.contentPageIdentifier;
    this.rejseplanenIdentifier = passengerType.rejseplanenIdentifier;
};

const PassengerTypeCountModel = function(passengerType, count) {
    this.passengerTypeId = passengerType.id;
    this.count = count;
};

const DEFAULT_MAXIMUM_PASSENGERS = 15;

export const state = {
    maximumPassengers: DEFAULT_MAXIMUM_PASSENGERS,
    passengerTypes: [],
    passengers: []
};

export const mutations = {
    updatePassengerTypes: (state, payload) => {
        state.passengerTypes = payload.passengerTypes.map(
            (passengerType, index) => {
                return new PassengerTypeModel(passengerType, index);
            }
        );
    },
    updatePassengers: (state, payload) => {
        const allowedPassengerTypeIds =
            payload.passengerTypeIds || state.passengerTypes.map(t => t.id);
        state.maximumPassengers =
            payload.maximumPassengers || DEFAULT_MAXIMUM_PASSENGERS;

        // check if there already is existing passengers selected, in the allowed range, otherwise
        // we will reset totally, so we get the default count added, this is mostly for handling
        // rejseplanen links, where we may bootstrap with a passenger type that is later removed, when
        // a specifc flow is started. I know, very complicated
        if (
            state.passengers.filter(
                p =>
                    allowedPassengerTypeIds.includes(p.passengerTypeId) &&
                    p.count > 0
            ).length > 0
        ) {
            const existingPassengers = state.passengers.filter(passenger => {
                return allowedPassengerTypeIds.includes(
                    passenger.passengerTypeId
                );
            });

            state.passengers = state.passengerTypes
                .filter(passengerType => {
                    return allowedPassengerTypeIds.includes(passengerType.id);
                })
                .map(passengerType => {
                    const existingPassenger = existingPassengers.filter(
                        existingPassenger => {
                            return (
                                existingPassenger.passengerTypeId ===
                                passengerType.id
                            );
                        }
                    )[0];

                    if (existingPassenger) {
                        return existingPassenger;
                    } else {
                        return new PassengerTypeCountModel(passengerType, 0);
                    }
                });
        } else {
            const allowedPassengerTypes = state.passengerTypes.filter(
                passengerType => {
                    return allowedPassengerTypeIds.includes(passengerType.id);
                }
            );

            // if this is the first time we update passengers, we need to set count to 1
            // this will first use a type id from the payload, next up it will try to find the adult
            // and last it will pick the first one
            let primary = null;
            if (payload.primaryId)
                primary = allowedPassengerTypes.filter(
                    t => t.id === payload.primaryId
                )[0];
            if (!primary)
                primary = allowedPassengerTypes.filter(
                    t => t.minimumAge <= 18 && t.maximumAge >= 18
                )[0];
            if (!primary) primary = allowedPassengerTypes[0];

            state.passengers = allowedPassengerTypes.map(passengerType => {
                return new PassengerTypeCountModel(
                    passengerType,
                    passengerType === primary ? 1 : 0
                );
            });
        }
    },
    updatePassengerCountForType: (state, payload) => {
        if (!Number.isInteger(payload.passengerTypeId)) {
            throw new Error("Must provider a passenger type id");
        }

        if (!Number.isInteger(payload.value)) {
            throw new Error("Value must be a number");
        }

        let passenger = state.passengers.filter(passenger => {
            return passenger.passengerTypeId === payload.passengerTypeId;
        })[0];

        if (passenger === undefined) {
            throw new Error(
                "Passenger " + passenger.passengerTypeId + " is not allowed"
            );
        }

        passenger.count = payload.value;
    },
    clearPassengers: state => {
        state.passengers = [];
    }
};

export const getters = {
    getPassengerTypes: state => {
        return state.passengerTypes;
    },
    getPassengerTypeById: state => {
        return passengerTypeId => {
            return state.passengerTypes.filter(passengerType => {
                return passengerType.id === passengerTypeId;
            })[0];
        };
    },
    getAllowedPassengerTypes: state => {
        const allowedPassengerTypeIds = state.passengers.map(passenger => {
            return passenger.passengerTypeId;
        });

        return state.passengerTypes.filter(passengerType => {
            return allowedPassengerTypeIds.includes(passengerType.id);
        });
    },
    getPrimaryPassengerType: (state, getters) => {
        const allowed = getters.getAllowedPassengerTypes;
        let primary = allowed.filter(
            t => t.minimumAge <= 18 && t.maximumAge >= 18
        )[0];
        if (!primary) primary = allowed[0];
        return primary;
    },
    getPassengers: state => {
        return state.passengers
            .filter(passenger => {
                return passenger.count > 0;
            })
            .map(passenger => {
                return {
                    passengerType: state.passengerTypes.filter(
                        passengerType => {
                            return (
                                passengerType.id === passenger.passengerTypeId
                            );
                        }
                    )[0],
                    count: passenger.count
                };
            });
    },
    getPassengerCountById: state => {
        return passengerTypeId => {
            let passengerTypeCount = state.passengers.filter(passenger => {
                return passenger.passengerTypeId === passengerTypeId;
            })[0];

            if (passengerTypeCount === undefined) {
                return 0;
            }

            return passengerTypeCount.count;
        };
    },
    getPassengerCount: state => {
        return state.passengers
            .map(passenger => {
                return passenger.count;
            })
            .reduce(
                (accumulator, currentValue) => accumulator + currentValue,
                0
            );
    },
    getMaximumPassengers: state => {
        return state.maximumPassengers;
    }
};

export const actions = {
    fetchPassengerTypeList: context => {
        return fetchPassengerTypeList()
            .then(response => {
                if (response.ok) {
                    return response.responseBody;
                }

                return Promise.reject(response);
            })
            .then(response => {
                context.commit("updatePassengerTypes", {
                    passengerTypes: response.passengerTypes
                });
            });
    },
    updatePassengerCountForType: (context, payload) => {
        // Update passenger count
        context.commit("updatePassengerCountForType", payload);

        return Promise.resolve();
    }
};
